import { useAtomValue } from 'jotai';
import {
  ChevronsLeftIcon,
  ChevronsRightIcon,
  HomeIcon,
  LayoutDashboardIcon,
  ScanLine,
  Settings,
  Sparkle,
  Sun,
  Video,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useMe } from '@/api/me';
import { useMyTenants } from '@/api/tenant';
import { SidebarLink } from '@/components/ui/sidebar-link';
import { currentTenantAtom } from '@/constants/currrentTenant';
import { env } from '@/constants/env';
import { findCurrentTenant } from '@/features/tenants/utils/find-tenant-with-parent';
import { useFlagPayload } from '@/hooks/use-flag';
import useResizeObserver from '@/hooks/use-resize-observer';
import { cn } from '@/lib/utils';
import { parseStringToBool } from '@/utils/parseStringToBool';

import { SidebarItem } from '../ui/sidebar-item';

type SidebarProps = {
  isVisible: boolean;
  closeMobileSidebarMenu: () => void;
};

type SidebarPart = 'dashboard' | 'sunlight-simulation' | 'image-rendering' | 'tools';

const SIDEBAR_COLLAPSED_KEY = 'sidebarCollapsed';

export const Sidebar = ({ isVisible, closeMobileSidebarMenu }: SidebarProps) => {
  const { t } = useTranslation();
  const isMobileView = !useResizeObserver(768);
  const currentTenant = useAtomValue(currentTenantAtom);
  const { data: tenants } = useMyTenants();
  const selectedTenant = useMemo(
    () => findCurrentTenant(tenants, currentTenant?.id),
    [tenants, currentTenant?.id]
  );
  const me = useMe({
    tenantId: selectedTenant?.id,
  });
  const flagPayload = useFlagPayload<Record<string, { hide: SidebarPart[] }>>('hide-sidebar-parts');

  const isPartHidden = (part: SidebarPart) => {
    if (!selectedTenant || !flagPayload) return false;

    return flagPayload[selectedTenant.id]?.hide.includes(part) ?? false;
  };

  const [isSidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() =>
    parseStringToBool(localStorage.getItem(SIDEBAR_COLLAPSED_KEY) || 'false')
  );

  const isCollapsed = isSidebarCollapsed && !isMobileView;
  const toggleSidebar = () => {
    setSidebarCollapsed((prev) => {
      const nextVal = !prev;
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, nextVal ? 'true' : 'false');
      return nextVal;
    });
  };

  const handleClick = (ev: EventTarget) => {
    if (isMobileView && !(ev as HTMLElement).closest('.ignore-click')) {
      closeMobileSidebarMenu();
    }
  };

  return (
    <aside
      className={cn(
        isVisible
          ? 'top-[60px] ease-linear max-md:left-0 max-md:w-full max-md:translate-x-0'
          : 'max-lg:absolute max-lg:left-0 max-lg:-translate-x-full',
        'z-50 h-[calc(100vh-60px)] w-full flex-col bg-transparent pb-6 transition-all duration-200 max-md:absolute max-md:bg-white max-md:p-4',
        isCollapsed ? 'max-w-[42px]' : 'max-w-[230px] max-md:max-w-[270px]'
      )}
    >
      <nav
        className="flex flex-col gap-1"
        onClick={(ev) => handleClick(ev.target)}
        onKeyUp={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            handleClick(e.target);
          }
        }}
      >
        <button
          type="button"
          onClick={toggleSidebar}
          className={cn(
            'ignore-click rounded-md hover:bg-primary-darker/10 max-md:hidden [&_svg]:size-6 [&_svg]:shrink-0 [&_svg]:text-primary',
            isCollapsed ? 'px-2' : 'px-6 max-md:px-2'
          )}
        >
          {isCollapsed ? <ChevronsRightIcon size={20} /> : <ChevronsLeftIcon size={20} />}
        </button>
        {!isPartHidden('dashboard') ? (
          <SidebarLink
            to="/"
            label={t('dashboard')}
            icon={<LayoutDashboardIcon />}
            isSidebarCollapsed={isCollapsed}
          />
        ) : null}
        <SidebarLink
          to="/estates"
          icon={<HomeIcon />}
          label={t('estates')}
          isSidebarCollapsed={isCollapsed}
        />
        {me.data.canAccessSetup && selectedTenant?.id ? (
          <SidebarLink
            to="/tenant/$tenantId/users"
            params={{ tenantId: selectedTenant.id }}
            icon={<Settings />}
            label={t('setup')}
            isSidebarCollapsed={isCollapsed}
          />
        ) : null}
        {!isPartHidden('tools') ? (
          <div className={cn('rounded-md bg-background-darker', isCollapsed && 'bg-transparent')}>
            {isCollapsed ? null : (
              <SidebarItem
                label={t('tools')}
                isSidebarCollapsed={isCollapsed}
                className="bg-secondary-dark rounded-b-none !text-primary-darker [&_svg]:!text-primary-lighter"
              />
            )}
            <SidebarLink
              to="/flatfinders"
              icon={<ScanLine />}
              label={t('flatfinderFormFlatfinder')}
              isSidebarCollapsed={isCollapsed}
            />
            {!isPartHidden('image-rendering') ? (
              <SidebarLink
                to="/image-rendering"
                icon={<Sparkle className="fill-current" />}
                label={t('rendering')}
                isSidebarCollapsed={isCollapsed}
              />
            ) : null}
            {parseStringToBool(env.IMAGE_RENDERING) && !isPartHidden('image-rendering') ? (
              <SidebarLink
                to="/image-to-video"
                icon={<Video />}
                label={t('image-to-video')}
                isSidebarCollapsed={isCollapsed}
              />
            ) : null}
            {!isPartHidden('sunlight-simulation') ? (
              <SidebarLink
                to="/shadowmap"
                icon={<Sun />}
                label={t('shadowmap-simulation')}
                isSidebarCollapsed={isCollapsed}
              />
            ) : null}
          </div>
        ) : null}
      </nav>
    </aside>
  );
};
